/**
 * French spell-check suggestion utility using nspell + dictionary-fr (Hunspell).
 *
 * Detects misspelled / unaccented words in free-text French fields (title,
 * description) and returns suggestion corrections in the same format used by
 * the chassis bulk-import fuzzy matching pipeline.
 */

let _spellCheckerPromise = null

/**
 * Lazily load nspell + French Hunspell dictionary once.
 * Dictionary files are served from /public/dictionaries/fr/ to avoid
 * the Node-only `dictionary-fr` package that uses node:fs/promises.
 */
export function loadSpellChecker() {
  if (_spellCheckerPromise) return _spellCheckerPromise

  _spellCheckerPromise = import('nspell').then(({ default: nspell }) => {
    const base = `${process.env.PUBLIC_URL}/dictionaries/fr`
    return Promise.all([
      fetch(`${base}/fr.aff`).then((r) => r.text()),
      fetch(`${base}/fr.dic`).then((r) => r.text()),
    ]).then(([aff, dic]) => nspell({ aff, dic }))
  })

  return _spellCheckerPromise
}

/**
 * Given a spell-checker instance and a text string, return a corrected version
 * (replacing each misspelled word with its top suggestion) or null if nothing
 * needs to change.
 */
export function suggestTextCorrection(spell, text) {
  if (!spell || !text || typeof text !== 'string') return null

  // Tokenise: capture word runs (including Unicode letters) and the gaps between
  const TOKEN_RE = /[\p{L}\p{M}]+/gu
  let changed = false
  const result = text.replace(TOKEN_RE, (word) => {
    if (spell.correct(word)) return word

    const suggestions = spell.suggest(word)
    if (!suggestions.length) return word

    // Skip all-caps tokens (likely abbreviations / codes)
    if (word === word.toUpperCase() && word.length > 1) return word

    const top = suggestions[0]

    // Only accept suggestion if it differs only in accents/diacritics
    // (avoid radical semantic changes from the spell checker)
    const normalize = (s) =>
      s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    if (normalize(top) !== normalize(word)) return word

    changed = true

    // Preserve original capitalisation
    if (word[0] === word[0].toUpperCase()) {
      return top[0].toUpperCase() + top.slice(1)
    }
    return top
  })

  return changed ? result : null
}

/**
 * Process an array of vehicle-type rows and return corrections for the `title`
 * and `description` fields.
 *
 * @param {object}  spell  - nspell instance (from loadSpellChecker)
 * @param {Array<{nItem, title, description}>} rows
 * @returns {Array<{row, field, originalValue, correctedValue, type}>}
 *   `row` is the 1-based Excel row number (data starts at row 2).
 */
export function processRowSuggestions(spell, rows) {
  if (!spell || !Array.isArray(rows)) return []
  const corrections = []

  rows.forEach((row, i) => {
    const excelRow = i + 2 // row 1 is the header

    ;['title', 'description'].forEach((field) => {
      const original = row[field]
      if (!original) return
      const corrected = suggestTextCorrection(spell, original)
      if (corrected) {
        corrections.push({
          row: excelRow,
          field,
          originalValue: original,
          correctedValue: corrected,
          type: 'suggestion',
        })
      }
    })
  })

  return corrections
}
